export type BackofficeReportReadFamily = 'list' | 'detail' | 'map' | 'heatmap';

export type BackofficeReportChangeSignalKind =
  | 'promotion'
  | 'reviewStatusChange'
  | 'speciesClassificationChange'
  | 'reviewMetadataChange'
  | 'deletion';

export type BackofficeReportChangedFact =
  | 'reportPresence'
  | 'reviewStatus'
  | 'speciesClassification'
  | 'reviewMetadata'
  | 'sightingLocation'
  | 'sightingDate';

export interface BackofficeReportChangeSignal {
  signalId: string;
  reportId: string;
  changeKind: BackofficeReportChangeSignalKind;
  occurredAtMs: number;
  expiresAtMs: number;
  changedFacts: BackofficeReportChangedFact[];
  affectedReadFamilies: BackofficeReportReadFamily[];
}

type ChangeKindContract = {
  changedFacts: BackofficeReportChangedFact[];
  affectedReadFamilies: BackofficeReportReadFamily[];
};

const CHANGE_KIND_CONTRACTS: Record<BackofficeReportChangeSignalKind, ChangeKindContract> = {
  promotion: {
    changedFacts: [
      'reportPresence',
      'reviewStatus',
      'speciesClassification',
      'sightingLocation',
      'sightingDate',
    ],
    affectedReadFamilies: ['list', 'detail', 'map', 'heatmap'],
  },
  reviewStatusChange: {
    changedFacts: ['reviewStatus'],
    affectedReadFamilies: ['list', 'detail', 'map', 'heatmap'],
  },
  speciesClassificationChange: {
    changedFacts: ['speciesClassification'],
    affectedReadFamilies: ['list', 'detail', 'map', 'heatmap'],
  },
  reviewMetadataChange: {
    changedFacts: ['reviewMetadata'],
    affectedReadFamilies: ['detail'],
  },
  deletion: {
    changedFacts: ['reportPresence'],
    affectedReadFamilies: ['list', 'detail', 'map', 'heatmap'],
  },
};

const ALLOWED_FACTS = new Set<BackofficeReportChangedFact>([
  'reportPresence',
  'reviewStatus',
  'speciesClassification',
  'reviewMetadata',
  'sightingLocation',
  'sightingDate',
]);

const ALLOWED_FAMILIES = new Set<BackofficeReportReadFamily>(['list', 'detail', 'map', 'heatmap']);

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeOccurrenceTime = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return value;
};

const normalizeStringArray = <T extends string>(
  value: unknown,
  allowedValues: Set<T>
): T[] | null => {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const normalized = value.map((item) => (typeof item === 'string' ? item : null));
  if (normalized.some((item) => item === null)) {
    return null;
  }

  if (!normalized.every((item): item is T => allowedValues.has(item as T))) {
    return null;
  }

  const deduped = [...new Set(normalized as T[])];
  return deduped.length === value.length ? deduped : null;
};

const hasSameStringSet = <T extends string>(left: T[], right: T[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item) => right.includes(item));
};

const normalizeChangeKind = (value: unknown): BackofficeReportChangeSignalKind | null => {
  if (
    value === 'promotion' ||
    value === 'reviewStatusChange' ||
    value === 'speciesClassificationChange' ||
    value === 'reviewMetadataChange' ||
    value === 'deletion'
  ) {
    return value;
  }

  return null;
};

export const normalizeBackofficeReportChangeSignal = (
  raw: unknown,
  fallbackSignalId?: string
): BackofficeReportChangeSignal | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const changeKind = normalizeChangeKind(record.changeKind);
  if (!changeKind) {
    return null;
  }

  const contract = CHANGE_KIND_CONTRACTS[changeKind];
  const signalId = normalizeString(record.signalId) ?? normalizeString(fallbackSignalId);
  const reportId = normalizeString(record.reportId);
  const occurredAtMs = normalizeOccurrenceTime(record.occurredAtMs);
  const expiresAtMs = normalizeOccurrenceTime(record.expiresAtMs);
  const changedFacts = normalizeStringArray(record.changedFacts, ALLOWED_FACTS);
  const affectedReadFamilies = normalizeStringArray(record.affectedReadFamilies, ALLOWED_FAMILIES);

  if (
    !signalId ||
    !reportId ||
    occurredAtMs === null ||
    expiresAtMs === null ||
    expiresAtMs <= Date.now() ||
    !changedFacts ||
    !affectedReadFamilies
  ) {
    return null;
  }

  if (
    !hasSameStringSet(changedFacts, contract.changedFacts) ||
    !hasSameStringSet(affectedReadFamilies, contract.affectedReadFamilies)
  ) {
    return null;
  }

  return {
    signalId,
    reportId,
    changeKind,
    occurredAtMs,
    expiresAtMs,
    changedFacts: [...contract.changedFacts],
    affectedReadFamilies: [...contract.affectedReadFamilies],
  };
};

export const getPromotionSignalFamilies = (): BackofficeReportReadFamily[] => [
  ...CHANGE_KIND_CONTRACTS.promotion.affectedReadFamilies,
];

export const getPromotionSignalChangedFacts = (): BackofficeReportChangedFact[] => [
  ...CHANGE_KIND_CONTRACTS.promotion.changedFacts,
];
